/**
 * @fileoverview Data fetching module for CXGN Table
 *
 * Handles AJAX data fetching with:
 * - Modern fetch API
 * - Automatic retry with exponential backoff
 * - Request cancellation
 * - Integrated caching
 * - Comprehensive error handling
 *
 * @module DataFetcher
 */

import { CacheManager } from './CacheManager.js';
import { DEFAULTS, ERROR_CODE, HTTP_STATUS } from './constants.js';

/**
 * @typedef {import('./types.js').AjaxConfig} AjaxConfig
 * @typedef {import('./types.js').TableError} TableError
 */

/**
 * DataFetcher handles all data loading operations
 * @class
 */
export class DataFetcher {
  /**
   * Create a DataFetcher instance
   * @param {Object} options - Configuration options
   * @param {AjaxConfig} options.ajax - AJAX configuration
   * @param {boolean} [options.cache=true] - Enable caching
   * @param {number} [options.cacheExpiry] - Cache expiry time
   * @param {number} [options.timeout] - Request timeout
   * @param {number} [options.maxRetries] - Maximum retry attempts
   * @param {function} [options.onSuccess] - Success callback
   * @param {function} [options.onError] - Error callback
   * @param {function} [options.onProgress] - Progress callback
   */
  constructor(options = {}) {
    this.ajax = options.ajax;
    this.timeout = options.timeout || DEFAULTS.AJAX_TIMEOUT;
    this.maxRetries = options.maxRetries !== undefined ? options.maxRetries : DEFAULTS.MAX_RETRIES;

    // Callbacks
    this.onSuccess = options.onSuccess;
    this.onError = options.onError;
    this.onProgress = options.onProgress;

    // Cache manager
    this.cacheManager = new CacheManager({
      enabled: options.cache !== false,
      expiry: options.cacheExpiry || DEFAULTS.CACHE_EXPIRY
    });

    // Request tracking
    this.currentRequest = null;
    this.abortController = null;

    // Metrics
    this.metrics = {
      requests: 0,
      successful: 0,
      failed: 0,
      retries: 0,
      totalTime: 0,
      cacheHits: 0,
      cacheMisses: 0
    };
  }

  /**
   * Fetch data from configured source
   * @param {number} [retryCount=0] - Current retry attempt
   * @returns {Promise<Array>} Fetched data
   */
  async fetch(retryCount = 0) {
    const startTime = performance.now();
    this.metrics.requests++;

    // Resolve the request parameters once per fetch, so a `data` callback is
    // invoked exactly as often as DataTables would invoke it.
    const data = this._resolveData();

    // Generate cache key
    const cacheKey = CacheManager.generateKey(this.ajax, data);

    // Check cache first
    const cachedData = this.cacheManager.get(cacheKey);
    if (cachedData !== null) {
      this.metrics.cacheHits++;
      console.log('DataFetcher: Cache hit');

      if (this.onSuccess) {
        this.onSuccess(cachedData, { fromCache: true });
      }

      return cachedData;
    }

    this.metrics.cacheMisses++;

    // Prepare fetch options
    const { url, options } = this._prepareFetchOptions(data);

    try {
      // Create AbortController for cancellation
      this.abortController = new AbortController();
      options.signal = this.abortController.signal;

      // Set timeout
      const timeoutId = setTimeout(() => {
        this.abortController.abort();
      }, this.timeout);

      // Perform fetch
      console.log(`DataFetcher: Fetching from ${url}${retryCount > 0 ? ` (retry ${retryCount})` : ''}`);

      const response = await fetch(url, options);

      clearTimeout(timeoutId);

      // Check response status
      if (!response.ok) {
        throw await this._createHttpError(response);
      }

      // Parse JSON
      const json = await response.json();

      // Extract data from response
      const data = this._extractData(json);

      // Validate data
      if (!Array.isArray(data)) {
        throw this._createError(
          ERROR_CODE.INVALID_RESPONSE,
          `Expected array but received ${typeof data}`,
          { response: json }
        );
      }

      // Cache the data
      this.cacheManager.set(cacheKey, data);

      // Update metrics
      this.metrics.successful++;
      this.metrics.totalTime += performance.now() - startTime;

      console.log(`DataFetcher: Fetched ${data.length} rows in ${Math.round(performance.now() - startTime)}ms`);

      // Call success callback
      if (this.onSuccess) {
        this.onSuccess(data, { fromCache: false });
      }

      return data;

    } catch (error) {
      // Handle different error types
      return this._handleError(error, retryCount, startTime);
    }
  }

  /**
   * Cancel current request
   * @returns {boolean} True if request was cancelled
   */
  cancel() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
      console.log('DataFetcher: Request cancelled');
      return true;
    }
    return false;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cacheManager.clear();
    console.log('DataFetcher: Cache cleared');
  }

  /**
   * Get fetch metrics
   * @returns {Object} Metrics
   */
  getMetrics() {
    const cacheMetrics = this.cacheManager.getMetrics();
    const avgTime = this.metrics.successful > 0
      ? this.metrics.totalTime / this.metrics.successful
      : 0;

    return {
      ...this.metrics,
      averageTime: Math.round(avgTime),
      successRate: this.metrics.requests > 0
        ? ((this.metrics.successful / this.metrics.requests) * 100).toFixed(2)
        : 0,
      cache: cacheMetrics
    };
  }

  /**
   * Resolve the ajax `data` option to a plain value
   *
   * Mirrors the DataTables contract: when `data` is a function it is handed the
   * request parameter object and may either mutate it in place or return a
   * replacement.
   *
   * @private
   * @returns {Object|string|undefined} Request parameters
   */
  _resolveData() {
    if (!this.ajax || typeof this.ajax !== 'object') {
      return undefined;
    }

    const data = this.ajax.data;

    if (typeof data !== 'function') {
      return data;
    }

    const params = {};
    const returned = data(params);

    return returned === undefined ? params : returned;
  }

  /**
   * Prepare fetch options from ajax config
   * @private
   * @param {Object|string} [data] - Pre-resolved request parameters
   * @returns {Object} { url, options }
   */
  _prepareFetchOptions(data = this._resolveData()) {
    let url, options = {};

    if (typeof this.ajax === 'string') {
      // Simple URL string
      url = this.ajax;
      options = {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      };
    } else if (typeof this.ajax === 'object') {
      // Full AJAX config
      url = this.ajax.url;
      const method = this.ajax.type || this.ajax.method || 'GET';

      options = {
        method: method.toUpperCase(),
        headers: {
          'Accept': 'application/json',
          ...(this.ajax.headers || {})
        }
      };

      // Add data/body
      const isGet = method.toUpperCase() === 'GET';

      if (isGet) {
        if (data) {
          // Append as query string
          const params = new URLSearchParams(data);
          url = `${url}${url.includes('?') ? '&' : '?'}${params.toString()}`;
        }
      } else {
        // Default to form encoding, which is what the Catalyst endpoints read
        // via $c->req->params(). Pass contentType explicitly to send JSON.
        const contentType = this.ajax.contentType === undefined
          ? 'application/x-www-form-urlencoded; charset=UTF-8'
          : this.ajax.contentType;

        if (contentType) {
          options.headers['Content-Type'] = contentType;
        }

        if (data !== undefined && data !== null) {
          if (typeof data === 'string') {
            options.body = data;
          } else if (contentType && contentType.indexOf('json') !== -1) {
            options.body = JSON.stringify(data);
          } else {
            options.body = new URLSearchParams(data).toString();
          }
        }
      }

      // Add credentials if specified
      if (this.ajax.credentials) {
        options.credentials = this.ajax.credentials;
      } else if (this.ajax.withCredentials) {
        options.credentials = 'include';
      }
    } else {
      throw this._createError(
        ERROR_CODE.AJAX_ERROR,
        'Invalid ajax configuration',
        { ajax: this.ajax }
      );
    }

    return { url, options };
  }

  /**
   * Extract data array from response
   * @private
   * @param {Object} response - JSON response
   * @returns {Array} Data array
   */
  _extractData(response) {
    // Honour the DataTables `ajax.dataSrc` option: a function receives the
    // whole payload, a string names the property holding the rows, and an
    // empty string means the payload itself is the row array.
    const dataSrc = this.ajax && typeof this.ajax === 'object' ? this.ajax.dataSrc : undefined;

    if (typeof dataSrc === 'function') {
      return dataSrc(response);
    }

    if (typeof dataSrc === 'string') {
      return dataSrc === '' ? response : response[dataSrc];
    }

    // Handle different response formats
    if (Array.isArray(response)) {
      return response;
    }

    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }

    if (response.aaData && Array.isArray(response.aaData)) {
      // DataTables server-side format
      return response.aaData;
    }

    // If response is object with unknown structure, throw error
    throw this._createError(
      ERROR_CODE.INVALID_RESPONSE,
      'Response does not contain data array',
      { response }
    );
  }

  /**
   * Handle fetch errors with retry logic
   * @private
   * @param {Error} error - Error object
   * @param {number} retryCount - Current retry count
   * @param {number} startTime - Request start time
   * @returns {Promise<Array>} Retried fetch or throws error
   */
  async _handleError(error, retryCount, startTime) {
    this.metrics.failed++;
    this.metrics.totalTime += performance.now() - startTime;

    // Don't retry if aborted
    if (error.name === 'AbortError') {
      console.log('DataFetcher: Request aborted');
      throw this._createError(ERROR_CODE.TIMEOUT, 'Request aborted', { original: error });
    }

    // Determine if error is retryable
    const isRetryable = this._isRetryableError(error);

    if (isRetryable && retryCount < this.maxRetries) {
      this.metrics.retries++;

      // Calculate backoff delay
      const delay = this._calculateBackoff(retryCount);

      console.log(`DataFetcher: Retrying in ${delay}ms (attempt ${retryCount + 1} of ${this.maxRetries})`);

      // Report progress
      if (this.onProgress) {
        this.onProgress({
          status: 'retrying',
          attempt: retryCount + 1,
          maxAttempts: this.maxRetries,
          delay
        });
      }

      // Wait and retry
      await this._sleep(delay);
      return this.fetch(retryCount + 1);

    } else {
      // Max retries exceeded or non-retryable error
      console.error('DataFetcher: Fetch failed', error);

      if (this.onError) {
        this.onError(error);
      }

      throw error;
    }
  }

  /**
   * Check if error is retryable
   * @private
   * @param {Error} error - Error object
   * @returns {boolean} True if retryable
   */
  _isRetryableError(error) {
    // Network errors are retryable
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return true;
    }

    // Timeout is retryable
    if (error.code === ERROR_CODE.TIMEOUT) {
      return true;
    }

    // 5xx server errors are retryable
    if (error.status >= 500 && error.status < 600) {
      return true;
    }

    // 429 (Too Many Requests) is retryable
    if (error.status === 429) {
      return true;
    }

    // 4xx client errors are NOT retryable (except 429)
    if (error.status >= 400 && error.status < 500) {
      return false;
    }

    return false;
  }

  /**
   * Calculate exponential backoff delay
   * @private
   * @param {number} retryCount - Retry attempt number
   * @returns {number} Delay in milliseconds
   */
  _calculateBackoff(retryCount) {
    const baseDelay = DEFAULTS.RETRY_BASE_DELAY;
    const maxDelay = DEFAULTS.RETRY_MAX_DELAY;

    // Exponential backoff: baseDelay * 2^retryCount
    const delay = Math.min(baseDelay * Math.pow(2, retryCount), maxDelay);

    // Add jitter (Â±20%) to prevent thundering herd
    const jitter = delay * 0.2 * (Math.random() - 0.5);

    return Math.round(delay + jitter);
  }

  /**
   * Sleep for specified duration
   * @private
   * @param {number} ms - Milliseconds
   * @returns {Promise<void>}
   */
  _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Create HTTP error from response
   * @private
   * @param {Response} response - Fetch response
   * @returns {Promise<TableError>} Error object
   */
  async _createHttpError(response) {
    let message;
    let code;

    switch (response.status) {
      case HTTP_STATUS.NOT_FOUND:
        code = ERROR_CODE.NOT_FOUND;
        message = 'Data endpoint not found (404)';
        break;
      case HTTP_STATUS.SERVER_ERROR:
        code = ERROR_CODE.SERVER_ERROR;
        message = 'Server error (500). Please try again later.';
        break;
      default:
        code = ERROR_CODE.AJAX_ERROR;
        message = `HTTP ${response.status}: ${response.statusText}`;
    }

    // Try to get error details from response body
    let details;
    try {
      details = await response.text();
    } catch (e) {
      details = null;
    }

    return this._createError(code, message, {
      status: response.status,
      statusText: response.statusText,
      details
    });
  }

  /**
   * Create standardized error object
   * @private
   * @param {string} code - Error code
   * @param {string} message - Error message
   * @param {Object} [context] - Additional context
   * @returns {TableError} Error object
   */
  _createError(code, message, context = {}) {
    const error = new Error(message);
    error.code = code;
    error.context = context;
    error.timestamp = Date.now();

    if (context.status) {
      error.status = context.status;
    }

    return error;
  }

  /**
   * Update AJAX configuration
   * @param {AjaxConfig} ajax - New AJAX config
   */
  setAjax(ajax) {
    this.ajax = ajax;
    // Clear cache when ajax config changes
    this.clearCache();
  }

  /**
   * Update timeout
   * @param {number} timeout - Timeout in milliseconds
   */
  setTimeout(timeout) {
    this.timeout = timeout;
  }

  /**
   * Update max retries
   * @param {number} maxRetries - Maximum retry attempts
   */
  setMaxRetries(maxRetries) {
    this.maxRetries = maxRetries;
  }

  /**
   * Enable/disable caching
   * @param {boolean} enabled - Enable caching
   */
  setCacheEnabled(enabled) {
    if (enabled) {
      this.cacheManager.enable();
    } else {
      this.cacheManager.disable(true);
    }
  }
}

export default DataFetcher;
